/**
 * Что открыто на экране — в терминах, понятных бэкенду.
 *
 * Здесь только маршрут: тип контекста, ключ и подпись для чипа. Отбор доски,
 * выделенный текст и прочее, что делает контекст богатым, приедет отдельно
 * (DEV-1575) — тред заводится по типу и ключу, и без них панель не начать.
 *
 * Данных задачи или доски в контексте нет намеренно: модель добирает их
 * инструментами от имени человека, а всё, что положить сюда, уедет в Gemini
 * независимо от того, спрашивали о нём или нет.
 */
import type { RouteLocationNormalizedLoaded } from 'vue-router'

import { TrackerAssistantContextType } from '@/api/clients/tracker-assistant'
import { i18n } from '@/i18n'

import { emptyContext, type AssistantContext } from './api'

const paramOf = (route: RouteLocationNormalizedLoaded, name: string): string => {
  const value = route.params[name]

  return Array.isArray(value) ? (value[0] ?? '') : (value ?? '')
}

/** Подпись экрана в чипе: «DEV · Доска», «DEV-1421». */
const labelOf = (routeName: string, key: string): string => {
  const screen = i18n.global.te(`assistant.context.${routeName}`)
    ? i18n.global.t(`assistant.context.${routeName}`)
    : ''

  if (!screen) return key

  return key ? `${key} · ${screen}` : screen
}

/**
 * Контекст открытой страницы.
 *
 * Имя проекта приходит снаружи: в адресе стоит ключ, а в чипе человек ждёт
 * увидеть «Разработка · Доска». Списка проектов у чистой функции нет и быть не
 * должно — она разбирает маршрут, а не ходит в сторы.
 */
export const routeContext = (
  route: RouteLocationNormalizedLoaded,
  projectName = '',
): AssistantContext => {
  const name = String(route.name ?? '')

  if (name === 'issue') {
    const key = paramOf(route, 'key')

    return {
      type: TrackerAssistantContextType.AssistantContextTypeIssue,
      key,
      label: key,
    }
  }

  if (['board', 'issues', 'epics', 'project-settings'].includes(name)) {
    const key = paramOf(route, 'key')

    return {
      type: TrackerAssistantContextType.AssistantContextTypeProject,
      key,
      label: labelOf(name, projectName || key),
    }
  }

  if (name === 'knowledge-page' || name === 'knowledge-page-edit') {
    const pageId = paramOf(route, 'pageId')

    return {
      type: TrackerAssistantContextType.AssistantContextTypePage,
      key: pageId,
      label: labelOf(name, ''),
    }
  }

  // Остальные экраны — разговор ни о чём конкретном: список задач, календарь,
  // настройки. Ключа у них нет, и выдумывать его нечем.
  return { ...emptyContext(), label: labelOf(name, '') }
}
